import React, { memo } from 'react';
import { preventNonNumericValue } from 'utils/helpers';
import { ValidationErrors } from 'constants/validations';
import { EqualIcon } from 'components/common/icons/equalIcon';
import { CloseIcon, DoneIcon } from 'components/common/icons';
import { Button, Col, Form, FormModal, InputNumber, Row, Select, Typography } from 'components';
import {
  SHIPPING_MEASUREMENT_RANGE_KG,
  SHIPPING_MEASUREMENT_RANGE_VOLUME,
  SHIPPING_METHOD_OPTIONS,
  SHIPPING_RATE_METRICS,
} from 'staticData/form/client/shipping';
import { useRatesAddEditModal } from './useRatesAddEditModal';
import { PriceRow } from './PriceRow';
import { SpecialRate } from '..';

const { Item } = Form;
const { Text } = Typography;

export interface IRatesAddEditModal {
  modalState: boolean;
  onModalStateChange: () => void;
  dataToEdit?: SpecialRate;
}

export const RatesAddEditModal = memo<IRatesAddEditModal>((props) => {
  const { modalState, dataToEdit, onModalStateChange } = props;
  const {
    onFinish,
    onCancel,
    onPriceRowRemove,
    onPriceRowEdit,
    onPriceSave,
    onPriceRowSave,
    onPriceRowCancel,
    onPriceCancel,
    warehouseData,
    form,
    contextHolder,
    prices,
    triggerValidation,
    editId,
    isChanged,
    isLoading,
    disableSaveBtn,
  } = useRatesAddEditModal({
    modalState,
    dataToEdit,
    onModalStateChange,
  });
  return (
    <>
      {contextHolder}
      <FormModal
        open={modalState}
        disableOkBtn={disableSaveBtn}
        isLoading={isLoading}
        form={form}
        title={`${dataToEdit ? 'Update' : 'Add'} Country Shipping Rates`}
        okText={dataToEdit ? 'Save' : 'Add'}
        width={680}
        onCancel={onCancel}
        onSubmit={onFinish}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item
              name="warehouseId"
              label={'Outbound Warehouse*'}
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Outbound Warehouse'),
                },
              ]}
            >
              <Select options={warehouseData} size={'large'} />
            </Item>
          </Col>
          <Col span={12}>
            <Item name="shippingMethods" label={'Shipping Method'}>
              <Select options={SHIPPING_METHOD_OPTIONS} size={'large'} />
            </Item>
          </Col>
          <Col span={24} className={'margin-t-8'}>
            <Row gutter={[0, 16]}>
              <Col span={24}>
                <Row justify={'space-between'} align={'middle'} gutter={[0, 16]}>
                  <Col>
                    <Text style={{ color: '#05796B', fontSize: '20px' }}>Price</Text>
                  </Col>
                  {!!prices.length && (
                    <Col span={24}>
                      <Row gutter={[0, 6]}>
                        {prices.map((_price) => (
                          <PriceRow
                            key={_price.measurementRange}
                            priceList={prices}
                            data={_price}
                            isEditeMode={_price.measurementRange === editId}
                            onPriceRowRemove={onPriceRowRemove}
                            onPriceRowEdit={onPriceRowEdit}
                            onPriceRowSave={onPriceRowSave}
                            onPriceRowCancel={onPriceRowCancel}
                          />
                        ))}
                      </Row>
                    </Col>
                  )}
                </Row>
              </Col>
              <Col span={24}>
                <Row gutter={[8, 0]} align={'top'} justify={'space-between'} className={'margin-t-6'}>
                  <Col span={8}>
                    <Item
                      name="metrics"
                      label={'Metrics'}
                      rules={[
                        {
                          required: triggerValidation || !prices.length,
                          message: ValidationErrors.required('Metrics'),
                        },
                      ]}
                    >
                      <Select
                        onChange={() => form.resetFields(['measurementRange'])}
                        options={SHIPPING_RATE_METRICS}
                        size={'large'}
                      />
                    </Item>
                  </Col>
                  <Col span={8}>
                    <Item
                      shouldUpdate={(prevValues: any, currentValues: any) =>
                        prevValues.metrics !== currentValues.metrics
                      }
                    >
                      {({ getFieldValue }) => {
                        const measurementRangeOptions =
                          getFieldValue('metrics') === 'KG'
                            ? SHIPPING_MEASUREMENT_RANGE_KG
                            : SHIPPING_MEASUREMENT_RANGE_VOLUME;
                        return (
                          <Item
                            name="measurementRange"
                            label={'Measurement Range'}
                            rules={[
                              {
                                required: triggerValidation || !prices.length,
                                message: ValidationErrors.required('Measurement Range'),
                              },
                            ]}
                          >
                            <Select
                              options={measurementRangeOptions.map((i: { value: string; label: string }) => ({
                                ...i,
                                disabled: prices.some((price) => price.measurementRange === i.value),
                              }))}
                              size={'large'}
                              disabled={!getFieldValue('metrics')}
                            />
                          </Item>
                        );
                      }}
                    </Item>
                  </Col>
                  <Col style={{ paddingTop: 32 }}>
                    <EqualIcon />
                  </Col>
                  <Col flex={'2'}>
                    <Item
                      name="price"
                      label={'Price'}
                      rules={[
                        {
                          required: triggerValidation || !prices.length,
                          message: ValidationErrors.required('Price'),
                        },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === '') {
                              return Promise.resolve();
                            }
                            const regex = /^\d+(\.\d{1,2})?$/;
                            if (!regex.test(value) || parseFloat(value) <= 0) {
                              return Promise.reject(
                                new Error('Price must be a positive number with up to 2 decimal points.')
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber<number>
                        controls={false}
                        size={'large'}
                        onBeforeInput={(event: any) => preventNonNumericValue(event)}
                        style={{ width: '100%' }}
                        suffix={<Text type={'secondary'}>€</Text>}
                      />
                    </Item>
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Row gutter={4} justify={'end'}>
                  <Col>
                    <Button
                      disabled={!isChanged}
                      color="default"
                      variant="filled"
                      onClick={onPriceCancel}
                      icon={<CloseIcon />}
                    />
                  </Col>
                  <Col>
                    <Button onClick={onPriceSave} disabled={!isChanged} type={'primary'} icon={<DoneIcon />} />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </FormModal>
    </>
  );
});
